import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useArealplaner, domainLabel, type Arealplan } from '@origo/planregister';
import { useMapViewContext } from '@origo/map-context';
import { Loader, Notice } from '@origo/origo-ui';
import PlanLandingPage, { type ActiveFilterChip } from './PlanLandingPage';
import { useCodelistOptions } from '../../hooks/useCodelistOptions';
import { usePlanFilter } from '../../filter/usePlanFilter';
import { emptyFilters, type PlanFilters } from '../../filter/types';
import { toPlanSummary } from '../../mappers/toPlanSummary';
import { getRecentPlanIds } from '../../utils/recentPlans';

interface PlanLandingContainerProps {
    onSelectPlan: (napId: string) => void;
    onCreatePlan: () => void;
}

const PlanLandingContainer: React.FC<PlanLandingContainerProps> = ({ onSelectPlan, onCreatePlan }) => {
    const { t } = useTranslation();
    const { mapView } = useMapViewContext();
    const [searchValue, setSearchValue] = useState('');
    const [filterOpen, setFilterOpen] = useState(false);

    const allPlans = useArealplaner();
    const codelists = useCodelistOptions();
    const plans = useMemo(() => allPlans.data ?? [], [allPlans.data]);

    const { filters, setFilters, sortOption, setSortOption, filteredPlans, activeFilterCount } =
        usePlanFilter(plans);

    // Fritekstsøk legges på toppen av de strukturerte filtrene.
    const searched = useMemo(() => {
        const q = searchValue.trim().toLowerCase();
        if (!q) return filteredPlans;
        return filteredPlans.filter(
            p =>
                p.plannavn.toLowerCase().includes(q) ||
                p.planId.toLowerCase().includes(q) ||
                p.nasjonalArealplanId.toLowerCase().includes(q)
        );
    }, [filteredPlans, searchValue]);

    // Tomt søk + ingen aktive filtre → vis «Nylig jobbet med». Ellers treff.
    const isSearching = searchValue.trim() !== '' || activeFilterCount > 0;

    const recentItems = useMemo(() => {
        const byNapId = new Map(plans.map(p => [p.nasjonalArealplanId, p]));
        return getRecentPlanIds()
            .map(id => byNapId.get(id))
            .filter((p): p is Arealplan => Boolean(p))
            .map(p => toPlanSummary(p, codelists.domains));
    }, [plans, codelists.domains]);

    const resultItems = useMemo(
        () => searched.map(plan => toPlanSummary(plan, codelists.domains)),
        [searched, codelists.domains]
    );

    // Aktive filtre som fjernbare chips (drømmeplan-admin-mønster).
    const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
        const chips: ActiveFilterChip[] = [];
        const patch = (p: Partial<PlanFilters>) => setFilters({ ...filters, ...p });
        const d = codelists.domains;

        filters.statuses.forEach(v =>
            chips.push({ id: `status-${v}`, label: `${t('landing.filter.status')}: ${domainLabel(d, 'planstatus', v)}`, onRemove: () => patch({ statuses: filters.statuses.filter(x => x !== v) }) })
        );
        filters.plantyper.forEach(v =>
            chips.push({ id: `plantype-${v}`, label: `${t('landing.filter.plantype')}: ${domainLabel(d, 'plantype', v)}`, onRemove: () => patch({ plantyper: filters.plantyper.filter(x => x !== v) }) })
        );
        filters.lovreferanser.forEach(v =>
            chips.push({ id: `lov-${v}`, label: `${t('landing.filter.lovreferanse')}: ${domainLabel(d, 'lovreferanse', v)}`, onRemove: () => patch({ lovreferanser: filters.lovreferanser.filter(x => x !== v) }) })
        );
        filters.kommuner.forEach(v =>
            chips.push({ id: `kommune-${v}`, label: `${t('landing.filter.kommune')}: ${v}`, onRemove: () => patch({ kommuner: filters.kommuner.filter(x => x !== v) }) })
        );
        if (filters.dateFrom || filters.dateTo) {
            chips.push({ id: 'date', label: t('landing.filter.dateLabel'), onRemove: () => patch({ dateFrom: null, dateTo: null }) });
        }
        return chips;
    }, [filters, setFilters, t, codelists.domains]);

    // Zoom-til kart. Arealplan er en table uten geometri; krever separat oppslag.
    // TODO(håvard): bygg FeatureLayer-query mot napId for geometri + mapView.goTo(...).
    const handleZoomTo = useCallback((napId: string) => {
        if (!mapView) return;
        mapView.graphics.removeAll();
        console.info('[planforvaltning] zoom-to (geometri ikke koblet enda)', napId);
    }, [mapView]);

    if (allPlans.isLoading) {
        return <Loader label={t('common.loading')} />;
    }
    if (allPlans.isError) {
        const detail = allPlans.error instanceof Error ? allPlans.error.message : String(allPlans.error);
        console.error('[planforvaltning] arealplan query error', allPlans.error);
        return (
            <Notice variant="danger">
                {t('common.errorLoading')} — {detail}
            </Notice>
        );
    }

    return (
        <PlanLandingPage
            onCreatePlan={onCreatePlan}
            search={{
                value: searchValue,
                onChange: setSearchValue,
                onClear: () => setSearchValue(''),
            }}
            filter={{
                plans,
                domains: codelists.domains,
                filters,
                onFiltersChange: setFilters,
                sortOption,
                onSortChange: setSortOption,
                showSortControl: false,
            }}
            filterOpen={filterOpen}
            onToggleFilter={() => setFilterOpen(o => !o)}
            activeFilterCount={activeFilterCount}
            activeFilterChips={activeFilterChips}
            onClearAllFilters={() => setFilters(emptyFilters)}
            sortOption={sortOption}
            onSortChange={setSortOption}
            isSearching={isSearching}
            recentItems={recentItems}
            resultItems={resultItems}
            totalCount={plans.length}
            onZoomTo={handleZoomTo}
            onSelect={onSelectPlan}
        />
    );
};

export default PlanLandingContainer;
